import { MixpanelEvents } from "@/constants/mixpanel-events";
import { aiChatModalOpenAtom } from "@/stores/chat.store";
import { cn } from "@/utils/cn";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { textVariants } from "@/utils/text-variants";
import { useSetAtom } from "jotai";
import { memo, useCallback } from "react";
import { SparkleButton } from "./sparkle-button";

/**
 * Temporary "Adding food..." card (Figma node 93:288) shown at the top of the
 * My Plan "Your Day" list while NutriCoach is adding food in the background.
 * Tapping it (or its sparkle) reopens the running chat without resetting it.
 */
export const AddingFoodCard = memo(function AddingFoodCard() {
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);

  const openChat = useCallback(() => {
    trackMixpanelEvent(MixpanelEvents.AI_CHAT_OPENED);
    openAiChatModal(true);
  }, [openAiChatModal]);

  const handleSparkleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      openChat();
    },
    [openChat],
  );

  return (
    <div
      data-testid="adding-food-card"
      className="shadow-meal-card rounded-2xl border border-[#E5E0F6] bg-white"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={openChat}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openChat();
          }
        }}
        className={cn(
          "flex cursor-pointer items-center justify-between gap-1 rounded-2xl p-4 pr-2 select-none",
        )}
      >
        <div className="flex flex-1 flex-col gap-3 overflow-hidden">
          <div aria-hidden className="shimmer-placeholder h-[22px] w-full rounded-[5px]" />
          <span
            role="status"
            aria-live="polite"
            className={cn(textVariants.footnote, "font-medium text-black opacity-60")}
          >
            Adding food...
          </span>
        </div>

        <div className="relative h-11 w-11">
          <SparkleButton aria-label="Open NutriCoach" onClick={handleSparkleClick} />
        </div>
      </div>
    </div>
  );
});
